import { MembershipShopBillingStart } from "@/types/enums/membership";

function datePartsInTimezone(date: Date, timezone?: string | null) {
  const parts = new Intl.DateTimeFormat("en-US", {
    // A tenant with no configured timezone falls back to UTC, not to the
    // reader's zone: the backend resolves the same absent value as
    // `ZoneInfo(tenant.default_timezone or "UTC")`. Letting it default to the
    // browser would put the console a day behind the backend for every tenant
    // whose `start_date` is an imported UTC-midnight calendar day.
    timeZone: timezone || "UTC",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return { year: value("year"), month: value("month"), day: value("day") };
}

/**
 * The calendar day a membership timestamp names *in the clinic's timezone*.
 *
 * This is the day the backend acts on: it stamps start_date with
 * `datetime.now(tenant_tz)` and reads it back as
 * `start_date.astimezone(tenant_tz).date()`. Resolving it in the viewer's zone
 * instead is wrong wherever the viewer is not in the clinic's zone — patient
 * portal readers have no timezone override at all, so they get the browser's.
 */
export function clinicCalendarDay(
  date: Date,
  timezone?: string | null,
): string {
  // formatToParts throws on an invalid Date where toLocaleDateString returns a
  // string, so guard the way every sibling formatter does.
  if (isNaN(date.getTime())) return "";
  const { year, month, day } = datePartsInTimezone(date, timezone);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Resolve the plan's configured billing start as a date-picker-safe calendar date. */
export function membershipBillingStartDate(
  policy: MembershipShopBillingStart | null | undefined,
  timezone?: string | null,
  now = new Date(),
): Date {
  const clinicToday = datePartsInTimezone(now, timezone);
  if (!policy || policy === MembershipShopBillingStart.ENROLLMENT_DAY) {
    return new Date(clinicToday.year, clinicToday.month - 1, clinicToday.day);
  }

  const targetDay =
    policy === MembershipShopBillingStart.FIRST_OF_MONTH ? 1 : 15;
  let year = clinicToday.year;
  let month = clinicToday.month;
  if (clinicToday.day >= targetDay) {
    month += 1;
    if (month === 13) {
      year += 1;
      month = 1;
    }
  }
  return new Date(year, month - 1, targetDay);
}

export function membershipEffectivelyRenews(
  autoRenew: boolean,
  renewalControlEnabled: boolean,
): boolean {
  return !renewalControlEnabled || autoRenew;
}

export function usesNoPaymentMembershipEnrollment(
  enrollmentTotalCents: number,
  hasPayableRenewal = false,
): boolean {
  return enrollmentTotalCents === 0 && !hasPayableRenewal;
}

function startOfCalendarDay(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

export function isPastCalendarDate(date: Date, now = new Date()): boolean {
  return startOfCalendarDay(date) < startOfCalendarDay(now);
}

export function isFutureCalendarDate(date: Date, now = new Date()): boolean {
  return startOfCalendarDay(date) > startOfCalendarDay(now);
}

/** Compare instants by their calendar dates in the clinic timezone. */
export function isPastCalendarDateInTimezone(
  date: Date,
  timezone?: string | null,
  now = new Date(),
): boolean {
  const target = datePartsInTimezone(date, timezone);
  const today = datePartsInTimezone(now, timezone);
  const targetKey = target.year * 10_000 + target.month * 100 + target.day;
  const todayKey = today.year * 10_000 + today.month * 100 + today.day;
  return targetKey < todayKey;
}

/** Compare a date-picker-safe target against today's date in the clinic. */
export function isFutureCalendarDateInTimezone(
  date: Date,
  timezone?: string | null,
  now = new Date(),
): boolean {
  const clinicToday = datePartsInTimezone(now, timezone);
  const targetKey =
    date.getFullYear() * 10_000 + (date.getMonth() + 1) * 100 + date.getDate();
  const clinicTodayKey =
    clinicToday.year * 10_000 + clinicToday.month * 100 + clinicToday.day;
  return targetKey > clinicTodayKey;
}
